import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
/**
 * Themed multi-select checklist — shared by the M6a composers
 * (playlist note picker, match-summary evidence pickers).
 *
 * Per the project's no-native-chrome rule, this is NOT a
 * `<select multiple>` — it's a scrollable list of toggle buttons
 * with an explicit checkbox affordance. Each option carries
 * `data-checklist-value` + `data-selected` for Playwright
 * targeting.
 */

export type CheckListItem<V extends string | number> = {
  value: V;
  label: string;
  selected: boolean;
};

export type CheckListProps<V extends string | number> = {
  "data-testid"?: string;
  emptyLabel: string;
  items: ReadonlyArray<CheckListItem<V>>;
  onToggle: (value: V) => void;
};

export function CheckList<V extends string | number>(props: CheckListProps<V>) {
  const collection = useCollection(props.items, {
    scope: props["data-testid"] ?? "checklist",
    text: (item) => item.label,
  });
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <CollectionToolbar collection={collection} label="evidence" />
      <div
        className="max-h-40 overflow-y-auto rounded border border-rule bg-surface p-1"
        data-testid={props["data-testid"]}
        role="group"
      >
        {props.items.length === 0 ? (
          <p className="text-ink-muted text-xs px-2 py-3">{props.emptyLabel}</p>
        ) : (
          collection.items.map((item) => (
            <button
              key={String(item.value)}
              type="button"
              onClick={() => props.onToggle(item.value)}
              aria-pressed={item.selected}
              data-checklist-value={String(item.value)}
              data-selected={item.selected}
              className={[
                "flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm transition-colors",
                item.selected
                  ? "bg-accent/15 text-ink"
                  : "text-ink-muted hover:bg-field hover:text-ink",
              ].join(" ")}
            >
              <span
                aria-hidden="true"
                className={[
                  "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[0.6rem]",
                  item.selected
                    ? "border-accent bg-accent text-accent-ink"
                    : "border-rule",
                ].join(" ")}
              >
                {item.selected ? "✓" : ""}
              </span>
              <span className="truncate">{item.label}</span>
            </button>
          ))
        )}
      </div>
      <CollectionPagination collection={collection} />
    </div>
  );
}
